import React, { useContext, useEffect, useState } from "react";
import { Box } from "../../../common/MaterialComponent";
import "./../IVRFlowConfiguration/IVRFlowConfiguration.css";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";
import ReactFlow, {
	Background,
	BackgroundVariant,
	Controls,
	Elements,
	Node,
	Edge,
	removeElements,
	ArrowHeadType,
} from "react-flow-renderer";

import {
	IVRContextType,
	IVRFlowWizardContext,
} from "../../../hooks/useIVRFlowWidgetConfiguration";
import ResponseMapModel from "../../../models/RequestForHelpTemplates/ResponseMapModel";
import { v4 as uuidv4 } from "uuid";

interface IWidget {
	nextAction: string;
	id: string;
	type: string;
	label: string;
	isValidated: boolean;
	responseMap: ResponseMapModel[];
}

interface IWidgetChildNode {
	widgetIndex: number;
	responseMapLength: number;
}

export const IVRStudioFlow: React.FC = () => {
	const strings = useGetComponentStringsContext().IVRStudioFlow;
	const [elements, setElements] = useState<Elements>([]);
	const { state, dispatch } = useContext<IVRContextType>(IVRFlowWizardContext);
	const nodeHeight: number = 90;
	const [tempWizardConfig, setTempWizardConfig] = useState<IWidget[]>([]);

	const createNode = (
		widget: IWidget,
		positionY: number,
		positionX: number
	) => {
		const newNode = {
			id: widget.id!,
			type:
				widget.type === "PickLanguage"
					? "input"
					: widget.type === "LogRequestForHelp"
					? "output"
					: "default",
			data: { label: widget.label },
			position: { x: positionX, y: positionY },
			className: !widget.isValidated ? "errorNode" : "",
		};
		return newNode;
	};
	const createEdge = (id: string, nextAction: string) => {
		const newEdge = {
			id: uuidv4(),
			source: id!,
			target: nextAction,
			arrowHeadType: ArrowHeadType.ArrowClosed,
		};
		return newEdge;
	};

	const moveNodeList = (
		arrayList: Elements,
		fromIndex: number,
		toIndex: number
	) => {
		const element = arrayList[fromIndex];
		arrayList.splice(fromIndex, 1);
		arrayList.splice(toIndex, 0, element);
		return arrayList;
	};

	useEffect(() => {
		let widgetList = Object.values(state.wizardConfig);
		widgetList = [].concat(...widgetList).filter((w) => !!w);
		const tempWidgetList = widgetList.map((x) => ({
			id: x.id,
			type: x.type,
			label: x.label,
			nextAction: x.nextAction,
			isValidated: x.isValidated,
			responseMap: x.responseMap ?? [],
		}));
		if (
			tempWizardConfig.length === tempWidgetList.length &&
			JSON.stringify(tempWizardConfig) === JSON.stringify(tempWidgetList)
		) {
			return;
		}
		setTempWizardConfig(JSON.parse(JSON.stringify(tempWidgetList)));

		// function to create node based on widget nextAction and responseMap nextAction
		const createNextActionNode = (
			widget: IWidget,
			widgetList: IWidget[],
			tempNodeList: string[]
		) => {
			const widgetChildNodeList: IWidgetChildNode[] = [];
			if (widget.nextAction) {
				widgetChildNodeList.push({
					widgetIndex: widgetList.findIndex(
						(el) => el.id === widget.nextAction
					),
					responseMapLength: 0,
				});
			}
			if (widget.responseMap && widget.responseMap.length > 0) {
				const responseMapNextAction: string[] = widget.responseMap.map(
					(x) => x.nextAction
				);
				if (responseMapNextAction.length > 0) {
					responseMapNextAction.forEach((nextAction) => {
						if (nextAction) {
							widgetChildNodeList.push({
								widgetIndex: widgetList.findIndex((el) => el.id === nextAction),
								responseMapLength: responseMapNextAction.length,
							});
						}
					});
				}
			}
			widgetChildNodeList.forEach((cn: IWidgetChildNode, index: number) => {
				const widgetIndex = cn.widgetIndex;
				if (widgetIndex > -1) {
					if (!tempNodeList.includes(widgetList[widgetIndex].id)) {
						const newNodeTemp = createNode(
							widgetList[widgetIndex],
							10 + tempNodeList.length * nodeHeight,
							cn.responseMapLength > 1
								? 200 - 200 / cn.responseMapLength + index * 50
								: 200 + index * 50
						);
						tempNodeList.push(widgetList[widgetIndex].id);
						setElements((elements: Elements) => elements.concat(newNodeTemp));
					} else {
						setElements((elements: Elements) => {
							const elementList = moveNodeList(
								elements,
								elements.findIndex((e) => e.id === widgetList[widgetIndex].id),
								elements.length
							);
							elementList.forEach((el: any, index: number) => {
								if (el && el.position) {
									el.position.y = 10 + index * nodeHeight;
								}
							});
							return elementList;
						});
					}
					if (
						widgetList[widgetIndex].nextAction ||
						(widgetList[widgetIndex].responseMap &&
							widgetList[widgetIndex].responseMap.length > 0 &&
							widgetList[widgetIndex].responseMap.filter((x) => x.nextAction))
					) {
						createNextActionNode(
							widgetList[widgetIndex],
							widgetList,
							tempNodeList
						);
					}
				}
			});
		};

		// sort list by orderId
		widgetList = widgetList.sort((a, b) => a.orderId - b.orderId);
		const tempNodeList: string[] = [];
		// reset element list
		setElements([]);
		// create nodes in react flow
		widgetList.forEach((widget: IWidget) => {
			if (!tempNodeList.includes(widget.id!)) {
				const newNode = createNode(
					widget,
					10 + tempNodeList.length * nodeHeight,
					200
				);
				tempNodeList.push(widget.id!);
				setElements((els) => els.concat(newNode));
			}
			if (
				(widget.id && widget.nextAction) ||
				(widget.responseMap &&
					widget.responseMap.length > 0 &&
					widget.responseMap.filter((x) => x.nextAction).length > 0)
			) {
				createNextActionNode(widget, widgetList, tempNodeList);
			}
		});

		// connect node with edges
		widgetList.forEach((widget: IWidget) => {
			const edgeList: Edge[] = [];
			if (widget.nextAction) {
				if (widget.nextAction) {
					const edge = createEdge(widget.id, widget.nextAction);
					edgeList.push(edge);
				}
			}
			if (
				widget.responseMap &&
				widget.responseMap.length > 0 &&
				widget.responseMap.filter((x) => x.nextAction).length > 0
			) {
				widget.responseMap.forEach((responseMap) => {
					if (responseMap.nextAction) {
						const edge = createEdge(widget.id, responseMap.nextAction);
						edgeList.push(edge);
					}
				});
			}
			setElements((elements: Elements) => {
				return elements.concat(edgeList);
			});
		});
		// need to be called only when wizardConfig value changed
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.wizardConfig]);

	const onElementsRemove = (elementsToRemove: Elements) =>
		setElements((els) => removeElements(elementsToRemove, els));

	const onElementClick = (
		event: React.MouseEvent<Element, MouseEvent>,
		element: Node<any> | Edge<any>
	) => {
		if (element && element.data) {
			// dispatch action to update widget detail
			dispatch({
				type: "SET_ACTIVE_STEPPER_ID",
				payload: {
					id: element.id,
				},
			});
		}
	};

	return (
		<>
			<Box mt={1} textAlign="center">
				{state.stepperContent.length > 0 ? (
					<div
						className="rectFlowContainer"
						style={{ height: state.stepperContent.length * nodeHeight }}
					>
						<ReactFlow
							elements={elements}
							onElementsRemove={onElementsRemove}
							onElementClick={onElementClick}
							deleteKeyCode=""
						>
							<Controls showInteractive={false} />
							<Background variant={BackgroundVariant.Lines} />
						</ReactFlow>
					</div>
				) : (
					<div>{strings.reactFlowHeader}</div>
				)}
			</Box>
		</>
	);
};
